import { gql } from '@apollo/client'
import {
	FRAGMENT_ADDRESS,
	FRAGMENT_ORDERS,
  FRAGMENT_PRODUCT_REQUIRED,
  FRAGMENT_VARIANT_REQUIRED,
} from './fragments'

export const GET_CUSTOMER = gql`
  query GET_CUSTOMER {
    customer {
      id
      bigcommerceId
      firstName
      lastName
      email
      phone
      paymentMethod
      vatNumber
      companyName
    }
  }
`

export const GET_CUSTOMERS_BY_VAT_NUMBER = gql`
  query GET_CUSTOMERS_BY_VAT_NUMBER($vatNumber: String!) {
    customers(vatNumber: $vatNumber) {
      edges {
        node {
          emailObfuscated
          vatNumber
          onboardingStatus
        }
      }
    }
  }
`

export const GET_CUSTOMER_ID = gql`
  query GET_CUSTOMER_ID {
    customer {
      id
    }
  }
`

export const GET_CUSTOMER_ADDRESSES = gql`
  query GET_CUSTOMER_ADDRESSES {
    customer {
      id
      bigcommerceId
      vatNumber
      shippingAddress {
        ...FragmentAddress
      }
      billingAddress {
        ...FragmentAddress
      }
    }
  }
  ${FRAGMENT_ADDRESS}
`

export const GET_CUSTOMER_ADDRESSES_HM = gql`
  query GET_CUSTOMER_ADDRESSES_HM {
    customer {
      id
      bigcommerceId
      vatNumber
      addresses {
        ...FragmentAddress
      }
    }
  }
  ${FRAGMENT_ADDRESS}
`

export const GET_CUSTOMER_ADDRESSES_IDS = gql`
  query GET_CUSTOMER_ADDRESSES_IDS {
    customer {
      id
      addresses {
        id
      }
    }
  }
`

export const GET_CUSTOMER_ORDERS = gql`
  query GET_CUSTOMER_ORDERS {
    customer {
      bigcommerceId
      firstName
      ...FragmentOrders
    }
  }
  ${FRAGMENT_ORDERS}
`

export const GET_CUSTOMER_SALES_PERSON = gql`
  query GET_CUSTOMER_SALES_PERSON {
    customer {
      salesperson {
        name
        email
        # phone
      }
    }
  }
`

export const GET_ORDER_BY_BC_ID = gql`
  query GET_ORDER_BY_BC_ID($bigcommerceId: Int!) {
    customer {
      bigcommerceId
    }
    order(bigcommerceId: $bigcommerceId) {
      bigcommerceId
      dateCreated
      deliveryDate
      paymentMethod
      subtotalExTax
      discountAmount
      couponDiscount
      shippingCostExTax
      totalIncTax
      totalTax
      orderLines {
        orderId
        basePrice
        quantity
        bigcommerceId
        priceExTax
        brandName
        productName
        # variantName
        variant {
          id
          name
          importStatus
          bigcommerceId
          media {
            medium(variant: "grid")
            mediaType
          }
          minSaleUnits
          stock
          stockWarning
        }
      }
      comments
      billingAddress {
        ...FragmentAddress
      }
      shippingAddress {
        ...FragmentAddress
      }
    }
  }
  ${FRAGMENT_ADDRESS}
`

export const GET_INVOICES = gql`
  query GET_INVOICES {
    customer {
      bigcommerceId
    }
    invoices {
      id
      number
      invoiceDate
      totalExTax
      invoiceLines {
        bigcommerceId
        dateCreated
        totalExTax
      }
    }
  }
`

export const GET_CUSTOMER_GRID_MY_PRODUCTS = gql`
  query GET_CUSTOMER_GRID_MY_PRODUCTS($first: Int!) {
    customer {
      bigcommerceId
      email
      myUsual(first: $first) {
        edges {
          cursor
          node {
            ...FragmentVariantRequired
            product {
              ...FragmentProductRequired
            }
          }
        }
      }
      categories {
        name
        slug
        subcategories {
          name
          slug
        }
      }
    }
  }
  ${FRAGMENT_VARIANT_REQUIRED}
  ${FRAGMENT_PRODUCT_REQUIRED}
`

export const LOGIN_MUTATION = gql`
  mutation LOGIN_MUTATION($email: String!, $password: String!) {
    loginMutation(input: { email: $email, password: $password }) {
      ok
      customer {
        bigcommerceId
        email
        firstName
        lastName
      }
      dyApiCustomerToken
      dyApiCustomerTokenExpiry
    }
  }
`

export const ADDRESS_UPDATE_MUTATION = gql`
  mutation ADDRESS_UPDATE_MUTATION(
    $id: Int!
    $firstName: String
    $lastName: String
    $street1: String
    $street2: String
    $city: String
    $countryCode: String
    $region: String
    $postalCode: String
  ) {
    addressUpdate(
      input: {
        address: {
          id: $id
          firstName: $firstName
          lastName: $lastName
          street1: $street1
          street2: $street2
          city: $city
          countryCode: $countryCode
          region: $region
          postalCode: $postalCode
        }
      }
    ) {
      ok
      errors
    }
  }
`

/**
 * $credentials is an object containing: { email, password }
**/
export const SIGN_UP_B2C_MUTATION = gql`
  mutation SIGNUP_MUTATION($firstName: String!, $lastName: String!,$credentials: AUTH_PROVIDER_CREDENTIALS!, $companyFields: CustomerB2cCompany) {
    customerRegister(
      input: {
        customer: {
          b2c: {
            firstName: $firstName
            lastName: $lastName
            companyFields: $companyFields
            credentials: $credentials
          }
        }
      }
    ) {
      ok
			dyApiCustomerToken
      dyApiCustomerTokenExpiry
      customer {
        bigcommerceId
        email
        firstName
        lastName
      }
    }
  }
`

export const SIGN_UP_B2B_MUTATION = gql`
  mutation SIGNUP_MUTATION($firstName: String!, $lastName: String!, $companyName: String!, $credentials: AUTH_PROVIDER_CREDENTIALS!, $formFields: CustomerB2bRegisterFormFieldsInput!, $addresses: [CustomerAddressesInput!]!) {
    customerRegister(
      input: {
        customer: {
          b2b: {
            firstName: $firstName
            lastName: $lastName
            companyName: $companyName
            credentials: $credentials
            formFields: $formFields
            addresses: $addresses
          }
        }
      }
    ) {
      ok
      errors
      token
      customer {
        bigcommerceId
      }
    }
  }
`

export const CUSTOMER_UPDATE_ADDRESSES_MUTATION = gql`
  mutation CUSTOMER_UPDATE_ADDRESSES_MUTATION(
    $addresses: [CustomerAddressesUpdateInput!]!
  ) {
    customerUpdateAddresses(input: { addresses: $addresses }) {
      ok
      errors
    }
  }
`

export const CUSTOMER_EMAIL_FORGOT_PASSWORD_MUTATION = gql`
  mutation CUSTOMER_EMAIL_FORGOT_PASSWORD($email: String!) {
    customerEmailResetPassword(
      input: {
        email: $email
      }
    ) {
      ok
      # customer {
      #   emailObfuscated
      # }
    }
  }
`

export const CUSTOMER_RESET_PASSWORD_MUTATION = gql`
  mutation CUSTOMER_RESET_PASSWORD($password: String!) {
    customerResetPassword(
      input: {
        password: $password
      }
    ) {
      ok
      # customer {
      #   bigcommerceId
      # }
      errors
    }
  }
`

export const CUSTOMER_ACTIVATE_ACCOUNT_MUTATION = gql`
  mutation CUSTOMER_ACTIVATE_ACCOUNT($password: String!) {
    customerActivate(
      input: {
        password: $password
      }
    ) {
      ok
      customer {
        bigcommerceId
      }
      errors
    }
  }
`




export const ADDRESS_CREATE_MUTATION = gql`
  mutation ADDRESS_CREATE_MUTATION(
    $street1: String!
    $street2: String!
    $city: String!
    $countryCode: String!
    $firstName: String!
    $lastName: String!
    $phone: String!
    $postalCode: String!
    $region: String!
  ) {
    addressCreate(
      input: {
        address: {
          street1: $street1
          street2: $street2
          city: $city
          region: $region
          countryCode: $countryCode
          postalCode: $postalCode
          firstName: $firstName
          lastName: $lastName
          phone: $phone
        }
      }
    ) {
      errors
      ok
      customer {
        addresses {
          ...FragmentAddress
        }
      }
    }
  }
  ${FRAGMENT_ADDRESS}
`

export const ADDRESS_DELETE_MUTATION = gql`
  mutation ADDRESS_DELETE_MUTATION($id: Int!) {
    addressDelete(input: { address: { id: $id } }) {
      errors
      ok
      customer {
        addresses {
          ...FragmentAddress
        }
      }
    }
  }
  ${FRAGMENT_ADDRESS}
`

export const CUSTOMER_UPDATE_MUTATION = gql`
  mutation CUSTOMER_UPDATE_MUTATION(
    $email: String!
    $password: String!
    $firstName: String!
    $lastName: String!
    $phone: String!
    $companyName: String!
    $vatNumber: String!
  ) {
    customerUpdate(
      input: {
        customer: {
          b2c: {
            email: $email
            password: $password
            firstName: $firstName
            lastName: $lastName
            phone: $phone
            companyFields: {
              companyName: $companyName
              vatNumber: $vatNumber
            }
          }
        }
      }
    ) {
      ok
      customer {
        bigcommerceId
        email
      }
      errors
    }
  }
`
